import {
  TextInput,
  required,
  SelectInput,
  ReferenceInput,
  SimpleFormIterator,
  ArrayInput,
  SimpleForm,
  useCreate,
  useUpdate,
  useSimpleFormIteratorItem,
  useTranslate,
  useRedirect,
  useNotify,
  useRecordContext,
  maxLength,
} from "react-admin";
import { Grid, Typography } from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { CampaignSubTypes, CampaignTypes, COURSES } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import Declarations from "../components/Declarations";
import Ibans from "../components/Ibans";
import { useLocation } from "react-router-dom";
import campaignHelper from "../helpers";
import CitizenshipInput from "../components/Inputs/CitizenshipInput";
import DirectionInput from "../components/Inputs/DirectionInput";
import Taxes from "../components/Taxes";
import { settings } from "app";
import CustomToolbar from "../components/CustomToolbar";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import { WeekDaysChoices } from "utils/choices";
import AdmissionMarkerInput from "../components/Inputs/AdmissionMarkerInput";
import { administrationRequests } from "api/administrationApi";
import CampaignTypeInput from "../components/Inputs/CampaignTypeInput";

const formatDate = date => {
  const correctTimeZoneOffset = 2 * 60;
  date = new Date(date);
  date.setMinutes(date.getMinutes() + correctTimeZoneOffset);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(date.getUTCMilliseconds()).padStart(7, "0");
  const timezoneOffset = "+02:00";

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezoneOffset}`;
};

const SaveForm = ({ isEdit, isCreate }) => {
  const [create] = useCreate();
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();

  const location = useLocation();
  const record = useRecordContext();
  const campaignIsStarted = record?.campaignIsStarted;

  const defaultValues = useMemo(() => {
    const params = new URLSearchParams(location.search);

    const source = params.get("source");
    if (source) {
      try {
        return JSON.parse(decodeURIComponent(source));
      } catch (e) {
        console.error("Invalid source:", e);
      }
    }
    return {};
  }, [location.search]);

  const [minEndDate, setMinEndDate] = useState(null);
  const [directionId, setDirectionId] = useState(defaultValues?.directionId || record?.directionId);
  const [typeId, setTypeId] = useState(null);
  const [citizenshipIds, setCitizenshipIds] = useState(defaultValues?.citizenship || []);
  const [isSaving, setIsSaving] = useState(false);
  const [courses, setCourses] = useState(null);

  const validateStartDate = async (value, allValues) => {
    if (!value) {
      return "Задължително";
    }
    if (value >= allValues.registrationTo) {
      return "Началната дата трябва да е по-малка от крайната";
    }
    if (isEdit) return;
    return await campaignHelper.checkAvailability({
      typeId,
      directionId,
      startDate: value,
      endDate: allValues.registrationTo,
      citizenshipIds,
      notify,
    });
  };

  const validateEndDate = async (value, allValues) => {
    if (!value) {
      return "Задължително";
    }
    if (value <= allValues.registrationFrom) {
      return "Крайната дата трябва да е по-голяма от началната";
    }
    if (isEdit) return;
    return await campaignHelper.checkAvailability({
      typeId,
      directionId,
      startDate: allValues.registrationFrom,
      endDate: value,
      citizenshipIds,
      notify,
    });
  };
  const handleStartDateChange = value => {
    setMinEndDate(value?._i || null);
  };
  const [excludedCoursesIds, setExcludedCoursesIds] = useState(record?.courses?.map(x => x.courseId) || []);

  const handleCoursesChange = Id => {
    if (excludedCoursesIds.includes(Id)) {
      setExcludedCoursesIds(excludedCoursesIds.filter(itemId => itemId !== Id));
    } else {
      setExcludedCoursesIds([...excludedCoursesIds, Id]);
    }
  };
  const campaignSave = data => {
    setIsSaving(true);

    const payload = {
      ...data,
      registrationFrom: formatDate(data.registrationFrom),
      registrationTo: formatDate(data.registrationTo),
    };

    if (isEdit) {
      update(
        candidateRequests.campaignByType(COURSES),
        {
          id: record.id,
          data: payload,
        },
        {
          onSuccess: () => {
            redirect("list", candidateRequests.campaignByType(COURSES));
          },
          onError: error => {
            notify(error.message || "Грешка при редакция", { type: "error" });
            setIsSaving(false);
          },
        },
      );
    } else {
      create(
        candidateRequests.campaignByType(COURSES),
        { data: payload },
        {
          onSuccess: () => {
            redirect("list", candidateRequests.campaignByType(COURSES));
          },
          onError: error => {
            notify(error.message || "Грешка при създаване", { type: "error" });
            setIsSaving(false);
          },
        },
      );
    }
  };

  const citizenshipChange = event => setCitizenshipIds(event.target.value);
  const handleTypeChange = event => setTypeId(event?.target?.value || event);
  const handleDirectionChange = newDirectionId => {
    setDirectionId(newDirectionId);
    if (!typeId || !directionId) {
      return;
    }
  };

  const Courses = ({ setCourses }) => {
    const { getValues } = useFormContext();
    const courseCount = getValues("courses")?.filter(x => x?.courseId && x?.courseId !== undefined)?.length;

    const CustomRemoveButton = () => {
      const translate = useTranslate();
      const { remove } = useSimpleFormIteratorItem();

      const handleClick = () => {
        remove();
        setExcludedCoursesIds(
          getValues("courses")
            ?.map(x => x?.courseId)
            .filter(x => x !== undefined),
        );
      };
      const title = translate("ra.action.remove");
      return (
        <Tooltip title={title}>
          <IconButton
            color="warning"
            onClick={handleClick}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      );
    };
    useEffect(() => {
      setCourses(courseCount);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseCount]);

    return (
      <Grid
        item
        xs={12}
      >
        <Typography color={"primary"}>Курсове</Typography>
        <ArrayInput
          source="courses"
          label=" "
          defaultValue={[{}]}
        >
          <SimpleFormIterator
            disableReordering
            disableClear
            disableRemove
            disableAdd={campaignIsStarted}
            inline
            sx={settings.style.iteratorWithoutGap}
          >
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={4}
              >
                <ReferenceInput
                  source="courseId"
                  reference={administrationRequests.getPreparatoryCoursesSelect()}
                  filter={{ ExcludeIds: excludedCoursesIds }}
                >
                  <SelectInput
                    readOnly={campaignIsStarted}
                    optionText="name"
                    label="Курс"
                    validate={required()}
                    onChange={event => handleCoursesChange(event.target.value)}
                    fullWidth
                  />
                </ReferenceInput>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  readOnly={campaignIsStarted}
                  source="location"
                  label="Място на провеждане"
                  validate={[required(), maxLength(100)]}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <SelectInput
                  readOnly={campaignIsStarted}
                  source="day"
                  label="Ден от седмицата"
                  choices={WeekDaysChoices}
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <CustomDateInput
                  readOnly={campaignIsStarted}
                  label="Започва от (дата)"
                  source="startDate"
                  noTime
                  required={true}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <CustomDateInput
                  readOnly={campaignIsStarted}
                  label="Приключва на (дата)"
                  source="endDate"
                  noTime
                  required={true}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <CustomTimeOnlyInput
                  readOnly={campaignIsStarted}
                  source="startTime"
                  label="Започва в (час)"
                  validate={required()}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <CustomTimeOnlyInput
                  readOnly={campaignIsStarted}
                  source="endTime"
                  label="Приключва в (час)"
                  validate={required()}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={1}
              >
                {!campaignIsStarted && <CustomRemoveButton />}
              </Grid>
            </Grid>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
    );
  };

  return (
    <SimpleForm
      onSubmit={campaignSave}
      defaultValues={defaultValues}
      toolbar={<CustomToolbar isSaving={isSaving} />}
    >
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          {isEdit &&
            (isEdit && campaignIsStarted ? (
              <Typography
                color={theme => theme.palette.success.main}
                sx={{ textAlign: "center", fontWeight: "bold" }}
              >
                Кампанията е стартирала
              </Typography>
            ) : (
              <Typography
                color={theme => theme.palette.success.main}
                sx={{ textAlign: "center", fontWeight: "bold" }}
              >
                Кампанията предстои да стартира
              </Typography>
            ))}
        </Grid>
        <Grid
          item
          xs={12}
        >
          <AdmissionMarkerInput readOnly={campaignIsStarted} />
        </Grid>
        <Grid
          item
          xs={8}
        >
          <TextInput
            label="Име на български език"
            source="nameBg"
            validate={required()}
          />
          <TextInput
            label="Име на английски език"
            source="nameEn"
            validate={required()}
          />
        </Grid>
        <Grid
          item
          xs={4}
        >
          <CitizenshipInput
            readOnly={campaignIsStarted}
            isArray
            isBgAndForeignersSeperated
            handleChange={citizenshipChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            readOnly={campaignIsStarted}
            label="Начало на регистрация"
            source="registrationFrom"
            required={true}
            validate={validateStartDate}
            onChange={event => {
              handleStartDateChange(event);
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Край на регистрация"
            source="registrationTo"
            required={true}
            validate={validateEndDate}
            inputProps={{
              min: minEndDate,
            }}
            helperText={false}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CampaignTypeInput
            ids={CampaignTypes.PreparatoryCourses}
            readOnly={campaignIsStarted}
            handleChange={handleTypeChange}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <DirectionInput
            readOnly={campaignIsStarted}
            required
            handleChange={handleDirectionChange}
            filterByUser
            filterOnlyForCampaigns
          />
        </Grid>
        {directionId && <Courses setCourses={setCourses} />}
        <Declarations
          citizenshipIds={citizenshipIds}
          isDisabled={campaignIsStarted}
        />
        <Taxes
          campaignSubTypeId={CampaignSubTypes.Preparatory}
          citizenshipIds={[1]}
          examsCount={courses}
          specialityIds={[]}
          isCreate={isCreate}
        />
        <Ibans />
      </Grid>
    </SimpleForm>
  );
};
export default SaveForm;
